import type { IFolderItem } from '../models/FolderItem';
import type { IIssue, IRuleOptions } from '../models/Issue';
import type { IScanResult } from '../models/ScanResult';
import type { IFileItem } from '../models/FileItem';
import { filePath, folderLabel, groupBy, stripCopyCounter, values } from './ruleUtils';

function nonRootFolders(scan: IScanResult): IFolderItem[] {
  return values(scan.folders).filter(f => f.id !== scan.rootId);
}

export function isProtected(scan: IScanResult, folderId: string): boolean {
  return !!scan.protectedFolders[folderId];
}

/**
 * Folders with no files anywhere below them. Only the topmost such folder is reported,
 * so a chain of empty folders is one issue rather than one per level.
 */
export function findEmptyFolders(scan: IScanResult): IIssue[] {
  return nonRootFolders(scan)
    // A protected folder (e.g. an empty Teams channel folder) is expected to exist even when empty.
    .filter(f => !isProtected(scan, f.id))
    .filter(f => f.totalFileCount === 0 &&
      (f.parentId === scan.rootId || scan.folders[f.parentId!].totalFileCount > 0 || isProtected(scan, f.parentId!)))
    .map(f => ({
      id: `emptyFolders:${f.id}`,
      category: 'emptyFolders' as const,
      severity: 'low' as const,
      message: f.childFolderIds.length > 0
        ? `"${folderLabel(f)}" has no files — only empty subfolders`
        : `"${folderLabel(f)}" is empty`,
      fileIds: [],
      folderIds: [f.id]
    }));
}

export function deepestBelow(scan: IScanResult, folder: IFolderItem): number {
  let deepest: number = folder.depth;
  for (const childId of folder.childFolderIds) {
    deepest = Math.max(deepest, deepestBelow(scan, scan.folders[childId]));
  }
  return deepest;
}

/**
 * Reports each subtree once, at the first folder past the threshold, rather than
 * every folder inside it — one actionable item per over-nested branch.
 */
export function findDeepNesting(scan: IScanResult, options: IRuleOptions): IIssue[] {
  return nonRootFolders(scan)
    .filter(f => f.depth === options.maxDepth + 1)
    .map(f => {
      const deepest: number = deepestBelow(scan, f);
      return {
        id: `deepNesting:${f.id}`,
        category: 'deepNesting' as const,
        severity: 'medium' as const,
        message: `"${folderLabel(f)}" is ${f.depth} levels deep` +
          (deepest > f.depth ? ` and goes down to level ${deepest}` : '') +
          ` (limit: ${options.maxDepth})`,
        fileIds: [],
        folderIds: [f.id]
      };
    });
}

/** Server-relative path of the library, e.g. '/sites/OrganizerTest/Shared Documents'. */
function libraryServerRelativePath(scan: IScanResult): string {
  const match: RegExpMatchArray | null = scan.library.webUrl.match(/^https?:\/\/[^/]+(\/.*)$/);
  if (!match) {
    return '';
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Files whose full path approaches SharePoint's 400-character limit, which breaks sync and migration. */
export function findLongPaths(scan: IScanResult, options: IRuleOptions): IIssue[] {
  const prefix: string = libraryServerRelativePath(scan);
  return values(scan.files)
    .map((f: IFileItem) => ({ file: f, length: `${prefix}/${filePath(f)}`.length }))
    .filter(x => x.length > options.maxPathLength)
    .map(x => ({
      id: `longPaths:${x.file.id}`,
      category: 'longPaths' as const,
      severity: 'high' as const,
      message: `Path is ${x.length} characters (SharePoint limit is 400): "${filePath(x.file)}"`,
      fileIds: [x.file.id],
      folderIds: []
    }));
}

/**
 * Library-wide size checks. Warns from 80% of the guideline so there is time to plan a split
 * (into more libraries or sites) before users hit slow views or sync problems.
 */
export function findLibraryLimits(scan: IScanResult, options: IRuleOptions): IIssue[] {
  const items: number = scan.fileCount + scan.folderCount;
  const issues: IIssue[] = [];
  const format = (n: number): string => n.toLocaleString();
  if (items > options.syncItemLimit) {
    issues.push({
      id: 'libraryLimits:sync', category: 'libraryLimits', severity: 'high',
      message: `This library has ${format(items)} items — more than the ${format(options.syncItemLimit)} OneDrive can reliably sync. ` +
        'People syncing it to their computers will run into errors. Split it into several libraries or sites.',
      fileIds: [], folderIds: []
    });
  } else if (items > options.libraryItemGuideline * 0.8) {
    const over: boolean = items > options.libraryItemGuideline;
    issues.push({
      id: 'libraryLimits:size', category: 'libraryLimits', severity: over ? 'high' : 'medium',
      message: `This library has ${format(items)} items${over ? ', more than' : ', approaching'} the ` +
        `${format(options.libraryItemGuideline)} that SharePoint experts recommend per library. ` +
        'Consider splitting it into several libraries or sites before views and sync slow down.',
      fileIds: [], folderIds: []
    });
  }
  return issues;
}

const ABBREVIATIONS: { [word: string]: string } = {
  hr: 'human resources',
  fin: 'finance',
  mktg: 'marketing',
  ops: 'operations',
  it: 'information technology',
  admin: 'administration',
  mgmt: 'management',
  dept: 'department'
};

const FILLER_WORDS: { [word: string]: boolean } = {
  documents: true, document: true, docs: true, doc: true, files: true, file: true,
  folder: true, folders: true, the: true, and: true, '&': true
};

/** Canonical form of a folder name: 'HR Documents' and 'Human Resource Documents' both become 'human resource'. */
export function normalizeFolderName(name: string): string {
  const words: string[] = stripCopyCounter(name.toLowerCase())
    .replace(/[_\-.]+/g, ' ')
    .split(/\s+/)
    .filter(w => !!w);

  const expanded: string[] = [];
  for (const word of words) {
    const full: string = ABBREVIATIONS[word] || word;
    for (const part of full.split(' ')) {
      if (!FILLER_WORDS[part]) {
        // Crude singularization so 'Resources' matches 'Resource'.
        expanded.push(part.length > 3 && /s$/.test(part) && !/ss$/.test(part) ? part.slice(0, -1) : part);
      }
    }
  }
  return expanded.join(' ');
}

/**
 * Sibling folders whose names mean the same thing. Only siblings are compared: repeated
 * names in different branches (e.g. each client's "Invoices") are normal structure.
 */
export function findSimilarFolders(scan: IScanResult): IIssue[] {
  const issues: IIssue[] = [];
  for (const parent of values(scan.folders)) {
    // Protected folders (e.g. Teams channel folders) are never consolidated.
    const siblings: IFolderItem[] = parent.childFolderIds.map(id => scan.folders[id]).filter(f => !isProtected(scan, f.id));
    const groups: IFolderItem[][] = values(groupBy(siblings, f => normalizeFolderName(f.name) || undefined))
      .filter(g => g.length > 1);
    for (const group of groups) {
      issues.push({
        id: `similarFolders:${group[0].id}`,
        category: 'similarFolders',
        severity: 'medium',
        message: `${group.length} folders look like the same thing: "${group.map(f => f.name).join('", "')}"` +
          (parent.path ? ` in "${parent.path}"` : ''),
        fileIds: [],
        folderIds: group.map(f => f.id)
      });
    }
  }
  return issues;
}

const CATCH_ALL_NAMES: { [name: string]: boolean } = {
  misc: true, miscellaneous: true, other: true, others: true, old: true, 'old stuff': true,
  stuff: true, temp: true, tmp: true, 'new folder': true, unsorted: true, 'to sort': true,
  random: true, general: true, junk: true, 'to be filed': true
};

export function isCatchAllFolderName(name: string): boolean {
  return !!CATCH_ALL_NAMES[stripCopyCounter(name.toLowerCase()).trim()];
}

/** Folders with vague names that tend to collect misfiled documents. */
export function findCatchAllFolders(scan: IScanResult): IIssue[] {
  return nonRootFolders(scan)
    .filter(f => isCatchAllFolderName(f.name))
    .map(f => ({
      id: `catchAllFolders:${f.id}`,
      category: 'catchAllFolders' as const,
      severity: 'medium' as const,
      message: `"${folderLabel(f)}" is a catch-all folder (${f.totalFileCount} ${f.totalFileCount === 1 ? 'file' : 'files'})`,
      fileIds: [],
      folderIds: [f.id]
    }));
}
